import { Switch as HerUISwitch, SwitchProps } from "@heroui/react";
import clsx from "clsx";
import { ReactNode, HTMLAttributes } from "react";

interface SwitchComponentProps extends Omit<SwitchProps, "children"> {
  label: string;
  description?: string;
  startContent?: ReactNode;
  isDraggable?: boolean;
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
  dragContentProps?: HTMLAttributes<HTMLDivElement>;
  dragIcon?: ReactNode;
}

export function Switch({
  label,
  description,
  startContent,
  isDraggable = false,
  dragHandleProps,
  dragContentProps,
  dragIcon,
  ...rest
}: SwitchComponentProps) {
  // For draggable items, use a custom layout instead of HerUISwitch children
  if (isDraggable) {
    return (
      <div
        className={clsx(
          "bg-content1 hover:bg-default inline-flex w-full max-w-md items-center",
          "min-w-full cursor-pointer justify-between gap-2 rounded-lg border-2 border-transparent px-2 py-1.5"
        )}
      >
        <div className="flex flex-1 items-center gap-2" {...dragContentProps}>
          {dragHandleProps && dragIcon && (
            <div {...dragHandleProps} className="shrink-0 cursor-grab active:cursor-grabbing">
              {dragIcon}
            </div>
          )}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {startContent}
            <div className="flex flex-col gap-0">
              <p className="text-sm">{label}</p>
              {description ? <p className="text-default-500 text-xs">{description}</p> : null}
            </div>
          </div>
        </div>
        <HerUISwitch
          classNames={{
            base: "m-0",
            thumb: "bg-background",
          }}
          size="sm"
          {...rest}
        />
      </div>
    );
  }

  // Standard Switch component for non-draggable items
  return (
    <HerUISwitch
      size="sm"
      classNames={{
        base: clsx(
          "inline-flex flex-row-reverse w-full max-w-md bg-content1 hover:bg-default items-center",
          "justify-between cursor-pointer rounded-lg gap-2 py-1.5 px-2 border-2 border-transparent min-w-full"
        ),
        label: "m-0",
        thumb: "bg-background",
      }}
      {...rest}
    >
      <div className="flex items-center gap-2">
        {startContent}
        <div className="flex flex-col gap-0">
          <p className="text-sm">{label}</p>
          {description ? <p className="text-default-500 text-xs">{description}</p> : null}
        </div>
      </div>
    </HerUISwitch>
  );
}
